import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useStudies } from "../hooks/useStudies";

export default function Category() {
  const { type } = useParams();
  const { studies: allStudies, loading } = useStudies();
  const [studies, setStudies] = useState([]);
  const [expandedTitles, setExpandedTitles] = useState({});

  useEffect(() => {
    if (loading) return;
    const lowerType = type.toLowerCase();
    const filtered = allStudies.filter((study) => {
      const category = study.Age_category?.toLowerCase();
      if (!category) return false;
      if (lowerType === "child") return category === "child" || category === "pediatric";
      return category === lowerType;
    });
    setStudies(filtered);
  }, [allStudies, loading, type]);

  const sanitize = (val) => (val && val !== "nan" ? val : "N/A");

  const prettyName = { adult: "Adult", child: "Paediatric", animal: "Preclinical (Animal)" }[type] || "Studies";

  const grouped = studies.reduce((acc, study) => {
    const title = sanitize(study.Article_title);
    if (!acc[title]) acc[title] = [];
    acc[title].push(study);
    return acc;
  }, {});

  const toggleExpand = (title) =>
    setExpandedTitles((prev) => ({ ...prev, [title]: !prev[title] }));

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#f8fafc", minHeight: "100vh" }}>
      {/* Page header */}
      <div style={{ background: "#0a2540", padding: "2.5rem 2rem" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#93c5e8", marginBottom: "0.5rem" }}>
            Browse
          </div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#fff", marginBottom: "0.5rem" }}>
            {prettyName} Epilepsy Studies
          </h1>
          <p style={{ fontSize: "0.9rem", color: "#93c5e8" }}>
            {loading ? "Loading…" : `${Object.keys(grouped).length} stud${Object.keys(grouped).length === 1 ? "y" : "ies"} · ${studies.length} comparison${studies.length === 1 ? "" : "s"}`}
          </p>
        </div>
      </div>

      {/* Study list */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem" }}>
        {loading ? (
          <p style={{ color: "#64748b" }}>Loading…</p>
        ) : Object.keys(grouped).length === 0 ? (
          <p style={{ color: "#64748b" }}>No studies found for this category.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {Object.entries(grouped).map(([title, rows]) => {
              const isExpanded = expandedTitles[title] || false;
              return (
                <div
                  key={title}
                  className="study-card"
                >
                  {/* Title row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: "0.975rem", fontWeight: 600, color: "#0a2540", marginBottom: "0.3rem", lineHeight: 1.45 }}>
                        {title}
                      </h3>
                      <p style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        {sanitize(rows[0].Authors)} · {sanitize(rows[0].Journal)} · {(rows[0].Year_of_publication || "—").toString().replace(/,/g, "")}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleExpand(title)}
                      style={{
                        flexShrink: 0,
                        background: "rgba(0,121,192,0.08)",
                        border: "none",
                        borderRadius: 6,
                        padding: "0.4rem 0.75rem",
                        cursor: "pointer",
                        color: "#0079C0",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                    >
                      {rows.length === 1 ? "View" : isExpanded ? "Hide" : `Show ${rows.length}`}
                    </button>
                  </div>

                  {/* Comparisons */}
                  {(rows.length === 1 || isExpanded) && (
                    <div style={{ marginTop: "0.75rem", borderTop: "1px solid #e2e8f0", paddingTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                      {rows.map((study) => (
                        <Link
                          key={study.id}
                          to={`/study/${study.id}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0.45rem 0.75rem",
                            borderRadius: 6,
                            background: "rgba(0,121,192,0.05)",
                            border: "1px solid rgba(0,121,192,0.1)",
                            textDecoration: "none",
                            color: "#1e293b",
                            fontSize: "0.85rem",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(0,121,192,0.1)"}
                          onMouseLeave={e => e.currentTarget.style.background = "rgba(0,121,192,0.05)"}
                        >
                          <span><strong>Comparison:</strong> {sanitize(study.Comparison)}</span>
                          <span style={{ color: "#0079C0", fontWeight: 600, marginLeft: "1rem", whiteSpace: "nowrap" }}>View →</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
